<template>
  <v-dialog
    v-model="dialog"
    max-width="500"
    :persistent="!closable"
    @input="onShowChange"
    style="max-height: 90vh; overflow-y: hidden"
  >
    <v-card class="glass-card">
      <v-card-text class="text-center py-6 px-6">
        <div class="d-flex flex-column align-center">
          <!-- تصویر لوگو -->
          <div class="modern-image-container mb-4">
            <v-img
              src="/media/img/logo.webp"
              lazy-src="/media/img/logo.webp"
              width="40"
              height="40"
              max-height="70"
              max-width="70"
              contain
              class="modern-image"
            />
          </div>
          <!-- عنوان -->
          <span class="d-block font-weight-bold font-size-h1 mb-2">{{
            title
          }}</span>

          <!-- پیام -->
          <span class="d-block mb-4 progress-text">{{ message }}</span>

          <!-- QR Code and Progress Bar -->
          <div class="progress-container">
            <div class="qr-wrapper">
              <svg
                :width="progressWidth"
                :height="qrSize + 30"
                :viewBox="`0 0 ${progressWidth} ${qrSize + 30}`"
                class="qr-svg"
              >
                <!-- Background - White -->
                <rect
                  :width="progressWidth"
                  :height="qrSize + 30"
                  fill="white"
                  rx="12"
                  stroke="#e0e0e0"
                  stroke-width="1"
                />

                <!-- Definitions for gradients -->
                <defs>
                  <!-- Rainbow gradient for progress bar -->
                  <linearGradient
                    id="progressGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stop-color="#ff6b6b" />
                    <stop offset="20%" stop-color="#feca57" />
                    <stop offset="40%" stop-color="#48dbfb" />
                    <stop offset="60%" stop-color="#0abde3" />
                    <stop offset="80%" stop-color="#54a0ff" />
                    <stop offset="100%" stop-color="#ff9ff3" />
                  </linearGradient>
                  <!-- QR Code color -->
                  <linearGradient
                    id="qrColorGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" :stop-color="getQrColor()" />
                    <stop offset="100%" :stop-color="getQrColor()" />
                  </linearGradient>
                </defs>

                <!-- QR Code Icon -->
                <g
                  class="qr-icon"
                  :style="{
                    opacity: getQrOpacity(),
                    transform: qrIconTransform,
                  }"
                >
                  <!-- QR Code Background - White -->
                  <rect
                    x="0"
                    y="0"
                    :width="qrSize"
                    :height="qrSize"
                    fill="white"
                    class="pulse"
                    rx="6"
                  />
                  <!-- QR Code Pattern (scaled from 21x21 to qrSize) -->
                  <g :transform="`translate(0, 0) scale(${qrSize / 21})`">
                    <!-- Top-left small square -->
                    <path d="M3,11H5V13H3V11Z" fill="url(#qrColorGradient)" />
                    <!-- Top-middle vertical rectangle -->
                    <path d="M11,5H13V9H11V5Z" fill="url(#qrColorGradient)" />
                    <!-- Middle cross-like shape -->
                    <path
                      d="M9,11H13V15H11V13H9V11Z"
                      fill="url(#qrColorGradient)"
                    />
                    <!-- Middle-right complex pattern -->
                    <path
                      d="M15,11H17V13H19V11H21V13H19V15H21V19H19V21H17V19H13V21H11V17H15V15H17V13H15V11Z"
                      fill="url(#qrColorGradient)"
                    />
                    <!-- Middle-right vertical rectangle -->
                    <path
                      d="M19,19V15H17V19H19Z"
                      fill="url(#qrColorGradient)"
                    />
                    <!-- Top-right finder pattern (outer) -->
                    <path d="M15,3H21V9H15V3Z" fill="url(#qrColorGradient)" />
                    <!-- Top-right finder pattern (inner) -->
                    <path d="M17,5V7H19V5H17Z" fill="white" />
                    <!-- Top-left finder pattern (outer) -->
                    <path d="M3,3H9V9H3V3Z" fill="url(#qrColorGradient)" />
                    <!-- Top-left finder pattern (inner) -->
                    <path d="M5,5V7H7V5H5Z" fill="white" />
                    <!-- Bottom-left finder pattern (outer) -->
                    <path d="M3,15H9V21H3V15Z" fill="url(#qrColorGradient)" />
                    <!-- Bottom-left finder pattern (inner) -->
                    <path d="M5,17V19H7V17H5Z" fill="white" />
                  </g>
                </g>

                <!-- Progress Bar -->
                <g
                  class="progress-bar"
                  :transform="`translate(0, ${qrSize + 10})`"
                >
                  <rect
                    x="0"
                    y="0"
                    :width="progressWidth"
                    height="16"
                    fill="#f0f0f0"
                    rx="8"
                    stroke="#e0e0e0"
                    stroke-width="1"
                  />
                  <rect
                    x="0"
                    y="0"
                    :width="(progress / 100) * progressWidth"
                    height="16"
                    fill="url(#progressGradient)"
                    rx="8"
                    class="progress-animation"
                  />
                  <text
                    :x="progressWidth / 2"
                    y="12"
                    text-anchor="middle"
                    class="percentage-text"
                    font-size="11"
                    font-weight="bold"
                    fill="#333"
                  >
                    %{{ persian(Math.round(progress)) }}
                  </text>
                </g>
              </svg>
            </div>
          </div>

          <!-- دکمه‌ها -->
          <div class="button-container">
            <v-btn
              v-for="(btn, index) in buttons"
              :key="index"
              @click="btn.click === 'close' ? close() : btn.click()"
              :color="btn.color || 'primary'"
              class="ma-1 button-style"
              depressed
            >
              {{ btn.text }}
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "ProgressModal2",
  props: {
    loading: { type: Boolean, default: false },
    show: { type: Boolean, default: false },
    closable: { type: Boolean, default: true },
    title: { type: String, default: "اطلاع‌رسانی" },
    message: { type: String, default: "در حال پردازش..." },
    data: { type: String, default: "" },
    image: { type: String, default: "/media/images/qrland-logo.png" },
    buttons: {
      type: Array,
      default: () => [{ text: "بستن", click: "close", color: "primary" }],
    },
    showProgress: { type: Boolean, default: false },
    duration: { type: Number, default: 10000 },
  },
  data() {
    return {
      dialog: false,
      progress: 0,
      intervalId: null,
      colorIntervalId: null,
      qrSize: 100,
      progressWidth: 400,
      colorIndex: 0,
    };
  },
  computed: {
    qrIconTransform() {
      const startPosition = 10;
      const maxMoveDistance = this.progressWidth - this.qrSize - 20;
      const moveDistance =
        startPosition + (this.progress / 100) * maxMoveDistance;
      return `translate(${moveDistance}px, 0)`;
    },
  },
  watch: {
    show(val) {
      this.dialog = val;
      if (val && this.showProgress) {
        this.startProgress(this.duration);
        this.startColorCycle();
      } else {
        this.stopProgress();
        this.stopColorCycle();
      }
    },
  },
  methods: {
       updateProgress(val, msg) {
      this.progress = val;
      if (msg) this.message = msg;
    },
    getQrColor() {
      // const colors = [
      //   "#ff6b6b",
      //   "#feca57",
      //   "#48dbfb",
      //   "#0abde3",
      //   "#54a0ff",
      //   "#ff9ff3",
      // ];
      const colors = [
        "#ff6b6b", // قرمز
        "#ff8c00", // نارنجی تیره
        "#feca57", // زرد
        "#00ff00", // سبز روشن
        "#00fa9a", // سبز میانی
        "#48dbfb", // فیروزه‌ای
        "#0abde3", // آبی روشن
        "#1e90ff", // آبی
        "#54a0ff", // آبی آسمانی
        "#ff00ff", // بنفش
        "#ff9ff3", // صورتی
        "#ff69b4", // صورتی تیره
      ];
      return colors[this.colorIndex];
    },
    getQrOpacity() {
      return Math.min(this.progress / 87, 1);
    },
    startColorCycle() {
      this.stopColorCycle();
      this.colorIndex = 0; // Reset color index
      this.colorIntervalId = setInterval(() => {
        this.colorIndex = (this.colorIndex + 1) % 12;
      }, 1200); // Match the 1.2s pulse animation
    },
    stopColorCycle() {
      if (this.colorIntervalId) {
        clearInterval(this.colorIntervalId);
        this.colorIntervalId = null;
      }
    },
    open({
      title = "اطلاع‌رسانی",
      message = "در حال پردازش...",
      buttons = [{ text: "بستن", click: "close", color: "primary" }],
      showProgress = true,
      duration = 10000,
    } = {}) {
      this.$emit("update:title", title);
      this.$emit("update:message", message);
      this.$emit("update:buttons", buttons);
      this.$emit("update:showProgress", showProgress);
      this.$emit("update:duration", duration);
      this.$emit("update:show", true);
      if (showProgress) {
        this.progress = 0;
        this.colorIndex = 0;
        this.startProgress(duration);
        this.startColorCycle();
      }
    },
    startProgress(duration) {
      this.stopProgress();
      const increment = 99 / (duration / 100);
      this.intervalId = setInterval(() => {
        if (this.progress < 99) {
          this.progress += increment;
        } else {
          this.progress = 99;
          this.stopProgress();
        }
      }, 100);
    },
    stopProgress() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },
    close() {
      this.stopProgress();
      this.stopColorCycle();
      this.progress = 0;
      this.colorIndex = 0;
      this.dialog = false;
      this.$emit("update:show", false);
    },
    complete() {
      this.progress = 100;
      this.stopProgress();
      this.stopColorCycle();
      setTimeout(() => {
        this.close();
      }, 500);
    },
    onShowChange(val) {
      this.$emit("onShowChange", val);
    },
  },
  beforeDestroy() {
    this.stopProgress();
    this.stopColorCycle();
  },
};
</script>

<style scoped>
.glass-card {
  background: rgba(255, 255, 255, 0.9) !important;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  max-height: 90vh;
  overflow-y: hidden;
  border-radius: 16px !important;
}

.modern-image-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.modern-image {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.15));
  transition: transform 0.3s ease;
}

.modern-image:hover {
  transform: scale(1.1);
}

.progress-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 0 auto;
}

.qr-wrapper {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
}

.qr-svg {
  filter: drop-shadow(0 3px 6px rgba(0, 0, 0, 0.1));
}

.qr-icon {
  transition: transform 0.5s ease-in-out;
}

.qr-icon path[fill="url(#qrColorGradient)"] {
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.07);
    opacity: 0.95;
  }
}

.progress-bar {
  transition: all 0.3s ease;
}

.progress-animation {
  transition: width 0.5s ease-in-out;
}

.percentage-text {
  font-family: var(--v-font-family, "Vazir", "IRANSans", sans-serif) !important;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
}

.progress-text {
  font-family: var(--v-font-family, "Vazir", "IRANSans", sans-serif) !important;
  font-size: 16px;
  font-weight: 500;
  color: #333;
  text-align: center;
  padding: 8px 16px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.font-size-h1 {
  font-size: 1.75rem !important;
  font-weight: 700 !important;
  color: #222;
}

.rightFlexDirection {
  direction: rtl;
}

.button-container {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.button-style {
  border-radius: 10px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  transition: all 0.3s ease;
  padding: 8px 20px !important;
}

.button-style:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

:deep(.v-card-text),
:deep(.v-btn),
:deep(.v-textarea) {
  font-family: var(--v-font-family, "Vazir", "IRANSans", sans-serif) !important;
}
</style>
